import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import apiService from '../../services/api';
import {
  nr2jStatusBadge,
  organizationDisplayName,
  contactDisplayName,
} from '../../utils/nr2jConstants';

const QUOTE_ORG_STATUSES = 'PROSPECT,CLIENT';
const SEARCH_TAKE = 30;
const DEBOUNCE_MS = 280;
const RECENT_TAKE = 8;

function contactLine(org) {
  const c = org?.primaryContact || org?.contacts?.[0];
  if (!c) return null;
  const name = contactDisplayName(c);
  const email = c.email || '';
  if (name && email) return `${name} · ${email}`;
  return name || email || null;
}

function statusLabel(org) {
  return nr2jStatusBadge(org?.commercialStatus).label;
}

/**
 * Combobox async société NR2J — pas de preload massif.
 * status = PROSPECT + CLIENT (INACTIVE exclu).
 */
export default function Nr2jOrgCombobox({
  value,
  selectedOrg,
  onChange,
  onCreateCompany,
  disabled = false,
  error = false,
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searched, setSearched] = useState(false);

  const displaySelected = selectedOrg
    ? organizationDisplayName(selectedOrg)
    : '';

  const runSearch = useCallback(async (rawQuery, { recent = false } = {}) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setSearchError('');
    try {
      const q = String(rawQuery || '').trim();
      const res = await apiService.listNr2jOrganizations({
        q: recent ? '' : q,
        status: QUOTE_ORG_STATUSES,
        take: recent ? RECENT_TAKE : SEARCH_TAKE,
      });
      if (seq !== reqSeq.current) return;
      setResults(res?.organizations || []);
      setSearched(true);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      setResults([]);
      setSearchError(err?.message || 'Recherche impossible');
      setSearched(true);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function openPanel() {
    if (disabled) return;
    setOpen(true);
    if (!searched && !query.trim()) {
      runSearch('', { recent: true });
    }
  }

  function onQueryChange(next) {
    setQuery(next);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(next, { recent: !String(next || '').trim() });
    }, DEBOUNCE_MS);
  }

  function pick(org) {
    onChange?.(org);
    setQuery('');
    setOpen(false);
    setResults([]);
    setSearched(false);
  }

  function clearSelection() {
    onChange?.(null);
    setQuery('');
    setResults([]);
    setSearched(false);
    setOpen(true);
    runSearch('', { recent: true });
  }

  const showEmptyCta = searched && !loading && !searchError && results.length === 0;

  return (
    <div ref={rootRef} className="relative" data-testid="nr2j-org-combobox">
      {value && selectedOrg && !open ? (
        <div
          className={`flex items-start justify-between gap-2 rounded-lg border px-3 py-2 ${
            error ? 'border-rose-500' : 'border-gray-300'
          }`}
        >
          <button
            type="button"
            className="min-w-0 flex-1 text-left"
            onClick={openPanel}
            disabled={disabled}
            data-testid="nr2j-org-combobox-selected"
          >
            <p className="truncate text-sm font-medium text-gray-900">
              {displaySelected}
            </p>
            <p className="truncate text-xs text-gray-500">
              {[selectedOrg.ville, statusLabel(selectedOrg)].filter(Boolean).join(' · ')}
            </p>
            {contactLine(selectedOrg) ? (
              <p className="truncate text-xs text-gray-500">{contactLine(selectedOrg)}</p>
            ) : null}
          </button>
          <button
            type="button"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            onClick={clearSelection}
            disabled={disabled}
            aria-label="Changer de société"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            disabled={disabled}
            placeholder="Rechercher une société…"
            className={`w-full rounded-lg border py-2 pl-9 pr-3 text-sm ${
              error ? 'border-rose-500' : 'border-gray-300'
            }`}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onFocus={openPanel}
            data-testid="nr2j-quote-org-select"
          />
        </div>
      )}

      {open ? (
        <div
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          data-testid="nr2j-org-combobox-list"
        >
          {loading ? (
            <p className="px-3 py-3 text-sm text-gray-500">Recherche…</p>
          ) : null}
          {searchError ? (
            <p className="px-3 py-3 text-sm text-rose-600">{searchError}</p>
          ) : null}
          {!loading && !searchError
            ? results.map((org) => (
              <button
                key={org.id}
                type="button"
                role="option"
                className="block w-full border-b border-gray-50 px-3 py-2.5 text-left last:border-0 hover:bg-gray-50"
                onClick={() => pick(org)}
              >
                <p className="text-sm font-medium text-gray-900">
                  {organizationDisplayName(org)}
                </p>
                <p className="text-xs text-gray-500">
                  {[org.ville, statusLabel(org)].filter(Boolean).join(' · ') || '—'}
                </p>
                {contactLine(org) ? (
                  <p className="text-xs text-gray-500">{contactLine(org)}</p>
                ) : null}
              </button>
            ))
            : null}
          {showEmptyCta ? (
            <div className="px-3 py-3">
              <p className="text-sm text-gray-600">Aucune société trouvée.</p>
              <button
                type="button"
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-900"
                onClick={() => {
                  setOpen(false);
                  onCreateCompany?.(query.trim());
                }}
                data-testid="nr2j-org-combobox-create"
              >
                <Plus className="h-4 w-4" />
                Créer une nouvelle société
              </button>
            </div>
          ) : null}
          {!loading && !searchError && results.length > 0 ? (
            <button
              type="button"
              className="flex w-full items-center gap-1.5 border-t border-gray-100 px-3 py-2.5 text-left text-sm font-medium text-indigo-700 hover:bg-indigo-50"
              onClick={() => {
                setOpen(false);
                onCreateCompany?.(query.trim());
              }}
            >
              <Plus className="h-4 w-4" />
              Créer une nouvelle société
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
